/**
 * iter695 — Extracted from PrintersSettingsPage.jsx.
 *
 * Scan-LAN / ePOS-Direct dialog. Two distinct flows fused into one
 * dialog (chosen at runtime via `iOSMode`):
 *
 *   Desktop / Android-Bridge mode
 *     "Scan LAN for printers" — talks to the local Print Bridge's
 *     /discover endpoint which does mDNS + TCP-9100 sweep. Discovered
 *     printers can be one-clicked into the Add drawer.
 *
 *   iPad / Safari mode
 *     "Add network printer (iPad)" — Safari can't scan the LAN, so we
 *     guide the user to type the printer IP, surface the AirPrint
 *     enable workflow, verify status via the Bridge (if reachable),
 *     and one-click AirPrint-fix any printer that's misconfigured.
 *
 * State strategy (different from sibling extractions):
 *   Most state is INTERNAL because every variable was only used inside
 *   this dialog body (verified by grep before extracting). The parent
 *   keeps `ipadVerifyState` only because the IPadOptionsDialog also
 *   reads it. Eight props in total — much cleaner than the 22 it would
 *   take if we kept everything parent-owned.
 *
 * Reset behaviour:
 *   When `open` becomes true, internal state is reset so a previous
 *   scan's results / errors / iPad form values don't bleed into the
 *   new session.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Radar, Info, ExternalLink, Loader2, XCircle, Plus, Printer,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import {
  discoverPrinters, verifyAirPrint, enableAirPrint, identifyPrinter,
  printViaAirPrint,
} from '@/lib/printBridge';
import {
  PRINTER_MODELS, findModelById, findModelByIdentity,
} from '@/lib/printerModels';

const INITIAL_IPAD_QUICK_FORM = {
  name: 'Counter - TM-m30II',
  ip_address: '',
  paper_width: '80mm',
  model: 'tm-m30iii',
};
const INITIAL_IPAD_FIX_STATE = { loading: false, result: null, error: null };

export default function EposDirectPanel({
  open,
  onOpenChange,
  iOSMode,
  ipadVerifyState,
  setIpadVerifyState,
  onPrefillFromScan,
  onPrinterAdded,
}) {
  // iter653 — Desktop scan state. Internalised — only the dialog reads it.
  const [scanRunning, setScanRunning] = useState(false);
  const [scanResults, setScanResults] = useState(null);
  const [scanError, setScanError] = useState(null);
  // iter658 — iPad Quick-Add by IP state. Internalised — only the dialog reads it.
  const [ipadQuickForm, setIpadQuickForm] = useState(INITIAL_IPAD_QUICK_FORM);
  const [ipadQuickSaving, setIpadQuickSaving] = useState(false);
  const [ipadAutoDetecting, setIpadAutoDetecting] = useState(false);
  // iter663 — Auto-enable AirPrint state. Internalised.
  const [ipadFixState, setIpadFixState] = useState(INITIAL_IPAD_FIX_STATE);

  // Reset whenever the dialog re-opens so we start clean.
  useEffect(() => {
    if (open) {
      setScanRunning(false);
      setScanResults(null);
      setScanError(null);
      setIpadFixState(INITIAL_IPAD_FIX_STATE);
      // NB: don't reset ipadQuickForm — the operator may want to keep
      // a typed IP across re-opens. They can clear it manually.
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="scan-lan-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Radar size={18} className="text-orange-500" />
            {iOSMode ? 'Add network printer (iPad)' : 'Scan LAN for printers'}
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            {iOSMode
              ? "Safari can't scan the LAN, but your TM-m30II is already broadcasting via AirPrint. Just paste its IP and we'll register it — Safari's print dialog will discover it automatically afterwards."
              : 'Discovers thermal printers on your local network via mDNS (Bonjour) and TCP-9100 sweep. Requires the Smarto Print Bridge running on this PC/Mac.'}
          </p>
        </DialogHeader>

        {iOSMode ? (
          <div className="space-y-3 py-1" data-testid="ipad-quick-add-panel">
            {/* iter661 — The #1 fix for "TM-m30II not showing in Safari's
                print picker" is enabling AirPrint in the printer's web
                admin. Surface this loudly at the top of the dialog. */}
            <div className="p-3 rounded-lg bg-rose-50 border-2 border-rose-300 text-xs space-y-2">
              <p className="font-bold text-rose-900 flex items-center gap-1.5">
                🔥 Printer not showing in Safari&apos;s print dialog?
              </p>
              <p className="text-rose-900 leading-relaxed">
                <strong>80% of the time, this fixes it:</strong> AirPrint ships <strong>OFF</strong> on the TM-m30II.
                Enter the IP below, then tap <strong>&ldquo;Open Printer Admin&rdquo;</strong>. Log in (default <code className="bg-white px-1 py-0.5 rounded">epson</code> / <code className="bg-white px-1 py-0.5 rounded">epson</code>)
                &rarr; <strong>Network &rarr; AirPrint &rarr; Enable &rarr; Save &rarr; Reboot</strong>.
              </p>
              <p className="text-rose-800 text-[11px]">
                After the printer reboots, come back here and tap <strong>&ldquo;Try AirPrint right now&rdquo;</strong> &mdash; the TM-m30II will appear in Safari&apos;s picker.
              </p>
            </div>

            <div className="p-3 rounded-lg bg-sky-50 border border-sky-200 text-xs space-y-1.5">
              <p className="font-semibold text-sky-900 flex items-center gap-1.5">
                <Info size={13} /> Find your printer&apos;s IP in 5 seconds
              </p>
              <ol className="list-decimal ml-5 space-y-0.5 text-sky-900">
                <li>Turn the TM-m30II OFF.</li>
                <li>Hold the <strong>Feed</strong> button, then turn it ON. Keep holding ~5s until it prints a self-test page.</li>
                <li>The printed slip shows the IP under <em>Ethernet / Wi-Fi</em>. Paste it below.</li>
              </ol>
              <p className="pt-1 text-sky-800">
                ✓ Works with Bluetooth-paired printers too — once paired to your iPad, AirPrint sends jobs to it without needing an IP. In that case just paste any LAN IP from the slip, or use <code>0.0.0.0</code> as a placeholder.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-[11px] font-medium text-muted-foreground mb-1 block">Name</label>
                <input
                  type="text"
                  className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                  placeholder="Counter - TM-m30II"
                  value={ipadQuickForm.name}
                  onChange={(e) => setIpadQuickForm({ ...ipadQuickForm, name: e.target.value })}
                  data-testid="ipad-quick-name"
                />
              </div>
              <div>
                <label className="text-[11px] font-medium text-muted-foreground mb-1 block">Printer IP</label>
                <div className="flex items-center gap-1.5">
                  <input
                    type="text"
                    className="flex-1 h-9 px-3 rounded-md border border-input bg-background text-sm font-mono"
                    placeholder="192.168.1.50"
                    value={ipadQuickForm.ip_address}
                    onChange={(e) => setIpadQuickForm({ ...ipadQuickForm, ip_address: e.target.value })}
                    data-testid="ipad-quick-ip"
                  />
                  {/* iter661 — One-tap entrypoint into the Epson web admin
                      so the user can flip AirPrint to Enable. */}
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={!ipadQuickForm.ip_address.trim()}
                    className="h-9 px-2 rounded-md border-rose-300 text-rose-700 hover:bg-rose-50 whitespace-nowrap"
                    title="Open the printer's web admin in a new tab so you can enable AirPrint"
                    onClick={() => {
                      const ip = ipadQuickForm.ip_address.trim();
                      if (!ip) return;
                      window.open(`http://${ip}/`, '_blank', 'noopener,noreferrer');
                    }}
                    data-testid="ipad-quick-open-admin"
                  >
                    <ExternalLink size={12} className="mr-1" />
                    Open Admin
                  </Button>
                </div>
              </div>
              <div>
                <label className="text-[11px] font-medium text-muted-foreground mb-1 block">Paper width</label>
                <select
                  className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                  value={ipadQuickForm.paper_width}
                  onChange={(e) => setIpadQuickForm({ ...ipadQuickForm, paper_width: e.target.value })}
                  data-testid="ipad-quick-width"
                >
                  <option value="80mm">80 mm</option>
                  <option value="58mm">58 mm</option>
                </select>
              </div>
            </div>

            {/* iter660 — Model picker + capability badges + auto-detect. */}
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <label className="text-[11px] font-medium text-muted-foreground">Printer model</label>
                <button
                  type="button"
                  disabled={!ipadQuickForm.ip_address.trim() || ipadAutoDetecting}
                  onClick={async () => {
                    setIpadAutoDetecting(true);
                    const r = await identifyPrinter(ipadQuickForm.ip_address.trim());
                    setIpadAutoDetecting(false);
                    if (!r.ok || !r.data?.ok) {
                      toast.error('Could not auto-detect. Bridge offline or printer admin port not reachable from this device. Pick the model manually below.');
                      return;
                    }
                    const ident = r.data.identity || r.data.model_hint || '';
                    const hit = findModelByIdentity(ident);
                    if (hit) {
                      setIpadQuickForm((f) => ({ ...f, model: hit.id }));
                      toast.success(`Detected: ${hit.label}`);
                    } else {
                      toast.warning(`Detected "${ident}" but it didn\u2019t match a known model. Pick the closest below.`);
                    }
                  }}
                  className="text-[11px] text-orange-600 hover:text-orange-700 disabled:text-muted-foreground/50 font-medium underline-offset-2 hover:underline"
                  data-testid="ipad-quick-autodetect"
                >
                  {ipadAutoDetecting ? 'Probing…' : 'Auto-detect via Bridge'}
                </button>
              </div>
              <select
                className="w-full h-9 px-3 rounded-md border border-input bg-background text-sm"
                value={ipadQuickForm.model}
                onChange={(e) => setIpadQuickForm({ ...ipadQuickForm, model: e.target.value })}
                data-testid="ipad-quick-model"
              >
                {PRINTER_MODELS.map((m) => (
                  <option key={m.id} value={m.id}>{m.label}</option>
                ))}
              </select>
              {(() => {
                const m = findModelById(ipadQuickForm.model);
                if (!m) return null;
                const cap = ({ on, label }) => (
                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${on === true ? 'bg-emerald-100 text-emerald-800' : on === false ? 'bg-rose-100 text-rose-800' : 'bg-muted text-muted-foreground'}`}>
                    {on === true ? '✓' : on === false ? '×' : '?'} {label}
                  </span>
                );
                return (
                  <div className="space-y-1.5" data-testid="ipad-quick-caps">
                    <div className="flex flex-wrap gap-1.5">
                      {cap({ on: m.airprint, label: 'AirPrint' })}
                      {cap({ on: m.bt, label: 'Bluetooth' })}
                      {cap({ on: m.net, label: 'Network' })}
                      {cap({ on: m.usb, label: 'USB' })}
                    </div>
                    {m.note && (
                      <p className={`text-[11px] ${m.airprint === false ? 'text-rose-700 font-medium' : 'text-muted-foreground'}`}>
                        {m.airprint === false && '⚠ '}{m.note}
                      </p>
                    )}
                    {m.firmware_toggle && (
                      <p className="text-[11px] text-amber-700">
                        ℹ AirPrint ships <strong>OFF</strong> on this model. Enable it via <code>http://{ipadQuickForm.ip_address || '<printer-IP>'}/</code> → Network → AirPrint → Enable → reboot.
                      </p>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* iter662 — Verify AirPrint status (Bridge v1.5.0+). */}
            <div className="space-y-1.5">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!ipadQuickForm.ip_address.trim() || ipadVerifyState.loading}
                className="w-full h-9 rounded-lg border-sky-300 text-sky-700 hover:bg-sky-50"
                onClick={async () => {
                  const ip = ipadQuickForm.ip_address.trim();
                  setIpadVerifyState({ loading: true, result: null, error: null });
                  const r = await verifyAirPrint(ip);
                  if (!r.ok || !r.data?.ok) {
                    setIpadVerifyState({
                      loading: false, result: null,
                      error: r.data?.error || 'Bridge not reachable. Tap "Open AirPrint status page" to verify manually.',
                    });
                    return;
                  }
                  setIpadVerifyState({ loading: false, result: r.data, error: null });
                }}
                data-testid="ipad-quick-verify-airprint"
              >
                {ipadVerifyState.loading ? (
                  <><Loader2 size={14} className="animate-spin mr-1.5" />Probing printer & Bonjour…</>
                ) : (
                  <><Radar size={14} className="mr-1.5" />Verify AirPrint status</>
                )}
              </Button>
              {ipadVerifyState.error && (
                <div className="p-2 rounded-md bg-amber-50 border border-amber-200 text-[11px] text-amber-900 space-y-1.5" data-testid="ipad-verify-error">
                  <p>{ipadVerifyState.error}</p>
                  <button
                    type="button"
                    className="text-sky-700 underline underline-offset-2 font-medium"
                    onClick={() => {
                      const ip = ipadQuickForm.ip_address.trim();
                      if (ip) window.open(`http://${ip}/PRESENTATION/ADVANCED/AIRPRINT/TOP`, '_blank', 'noopener,noreferrer');
                    }}
                    data-testid="ipad-verify-open-status"
                  >
                    → Open AirPrint status page on the printer
                  </button>
                </div>
              )}
              {ipadVerifyState.result && (() => {
                const r = ipadVerifyState.result;
                const admin = r.airprint_admin_state;
                const bonjour = r.bonjour_advertising;
                let tone = 'amber';
                let headline = 'Mixed signals — see details';
                if (admin === 'enabled' && bonjour === true) {
                  tone = 'emerald';
                  headline = 'AirPrint enabled & advertising on the LAN';
                } else if (admin === 'disabled' || bonjour === false) {
                  tone = 'rose';
                  headline = admin === 'disabled' ? 'AirPrint is OFF in the printer admin' : 'Printer is NOT advertising on Bonjour';
                } else if (admin === 'enabled' && bonjour === null) {
                  tone = 'sky';
                  headline = 'Admin says AirPrint is Enabled (Bonjour check skipped — install zeroconf on Bridge)';
                }
                const toneCls = {
                  emerald: 'bg-emerald-50 border-emerald-300 text-emerald-900',
                  rose:    'bg-rose-50 border-rose-300 text-rose-900',
                  amber:   'bg-amber-50 border-amber-300 text-amber-900',
                  sky:     'bg-sky-50 border-sky-300 text-sky-900',
                }[tone];
                // iter663 — "Fix it for me" CTA when AirPrint is disabled in admin.
                const canAutoFix = admin === 'disabled';
                return (
                  <div className={`p-2.5 rounded-md border-2 text-[11px] space-y-1.5 ${toneCls}`} data-testid="ipad-verify-result">
                    <p className="font-bold flex items-center gap-1.5">
                      {tone === 'emerald' ? '✓' : tone === 'rose' ? '✗' : 'ℹ'} {headline}
                    </p>
                    <p className="leading-relaxed">{r.details}</p>
                    {r.hostname && <p className="text-[10px] opacity-80">Advertised as: <code>{r.hostname}</code></p>}
                    {canAutoFix && !ipadFixState.result && (
                      <Button
                        type="button"
                        size="sm"
                        disabled={ipadFixState.loading}
                        className="w-full h-9 mt-1 rounded-lg bg-rose-600 hover:bg-rose-700 text-white"
                        onClick={async () => {
                          const ip = ipadQuickForm.ip_address.trim();
                          setIpadFixState({ loading: true, result: null, error: null });
                          const f = await enableAirPrint(ip);
                          if (!f.ok || !f.data?.ok) {
                            setIpadFixState({
                              loading: false, result: null,
                              error: f.data?.hint || 'Could not auto-enable AirPrint. Open the admin manually.',
                            });
                            return;
                          }
                          setIpadFixState({ loading: false, result: f.data, error: null });
                          toast.success('AirPrint enabled · printer rebooting');
                          // Auto re-verify in 30s so user sees the green pill.
                          setTimeout(async () => {
                            const v = await verifyAirPrint(ip);
                            if (v.ok && v.data?.ok) {
                              setIpadVerifyState({ loading: false, result: v.data, error: null });
                            }
                          }, 30000);
                        }}
                        data-testid="ipad-fix-airprint"
                      >
                        {ipadFixState.loading ? (
                          <><Loader2 size={14} className="animate-spin mr-1.5" />Enabling AirPrint & rebooting…</>
                        ) : (
                          <>🔧 Fix it for me (enable AirPrint + reboot)</>
                        )}
                      </Button>
                    )}
                    {ipadFixState.result && (
                      <div className="mt-1 p-2 rounded bg-white/60 text-emerald-900 border border-emerald-300" data-testid="ipad-fix-result">
                        <p className="font-semibold flex items-center gap-1.5">✓ {ipadFixState.result.succeeded ? 'AirPrint Enable command accepted' : 'Attempted'}</p>
                        <p className="leading-relaxed mt-0.5">{ipadFixState.result.hint}</p>
                        {ipadFixState.result.rebooted && <p className="text-[10px] opacity-80 mt-1">Re-verifying in 30s…</p>}
                      </div>
                    )}
                    {ipadFixState.error && (
                      <p className="mt-1 text-rose-900 font-medium">{ipadFixState.error}</p>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* iter659 — Try AirPrint right now from this dialog. */}
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="flex-1 h-9 rounded-lg border-orange-300 text-orange-700 hover:bg-orange-50"
                onClick={() => {
                  try {
                    const sample = {
                      company: 'Smarto', title: 'AirPrint TEST',
                      order_number: 'TEST-' + Date.now().toString().slice(-4),
                      date: new Date().toLocaleString(),
                      items: [
                        { qty: 1, name: 'AirPrint Discovery Probe', subtotal: 0 },
                      ],
                      subtotal: 0, tax: 0, total: 0, currency: '',
                      footer: 'If you can see your TM-m30II in Safari\u2019s print picker, AirPrint is working. If not, scroll down for fixes.',
                    };
                    const r = printViaAirPrint({ paper_width: ipadQuickForm.paper_width }, sample);
                    if (!r.ok) toast.error('Could not open the print dialog. Allow pop-ups for this site in Safari Settings.');
                  } catch (e) {
                    toast.error('Print dialog failed: ' + String(e));
                  }
                }}
                data-testid="ipad-quick-test-airprint"
              >
                <Printer size={14} className="mr-1.5" />
                Try AirPrint right now
              </Button>
            </div>

            {/* iter659 — Troubleshooter expander. */}
            <details className="text-xs rounded-lg border border-amber-200 bg-amber-50/50 px-3 py-2" data-testid="ipad-airprint-troubleshooter">
              <summary className="cursor-pointer font-semibold text-amber-900 select-none">
                Printer not showing in Safari&apos;s print dialog?
              </summary>
              <ol className="list-decimal ml-5 mt-2 space-y-1.5 text-amber-900">
                <li><strong>Same Wi-Fi?</strong> iPad <em>Settings → Wi-Fi</em> and the printer&apos;s self-test slip must show the same SSID. Guest Wi-Fi networks usually block AirPrint discovery.</li>
                <li><strong>AirPrint enabled on the TM-m30II?</strong> Visit <code>http://{'<printer-IP>'}/</code> in Safari → log in (default <code>epson</code>/<code>epson</code>) → <em>Network → AirPrint</em> → set to <strong>Enable</strong> → reboot the printer.</li>
                <li><strong>Model check:</strong> only TM-m30III, TM-m30II-NT, and TM-m30II with the Epson AirPrint firmware support AirPrint. The Bluetooth-only TM-m30II-BT does NOT support AirPrint and cannot print from Safari — use a PC Bridge or Lantronix instead.</li>
                <li><strong>Bonjour / mDNS blocked?</strong> Many corporate routers and mesh systems block multicast. Disable AP isolation / client isolation on the Wi-Fi router, and ensure mDNS (UDP 5353) is allowed across SSIDs.</li>
                <li><strong>Bluetooth-paired printers:</strong> open iPad <em>Settings → Bluetooth</em> and confirm the TM-m30II appears as <em>Connected</em>. Once OS-paired, AirPrint will list it automatically.</li>
                <li><strong>Last resort:</strong> install a Lantronix xPrintServer Network on the LAN. It exposes any Epson printer (Bluetooth or USB) as an AirPrint device.</li>
              </ol>
            </details>
          </div>
        ) : (
          <div className="space-y-3 py-2 min-h-[180px]">
            {!scanResults && !scanRunning && !scanError && (
              <div className="text-center py-6 space-y-3">
                <Radar size={36} className="mx-auto text-muted-foreground/60" />
                <p className="text-xs text-muted-foreground max-w-xs mx-auto">Click <strong>Start scan</strong>. This takes ~5-15 seconds. Tip: install <code>zeroconf</code> on the bridge for AirPrint discovery (<code>pip install zeroconf</code>).</p>
              </div>
            )}
            {scanRunning && (
              <div className="text-center py-8 space-y-2">
                <Loader2 size={28} className="mx-auto animate-spin text-orange-500" />
                <p className="text-xs text-muted-foreground">Scanning your local network…</p>
              </div>
            )}
            {scanError && (
              <div className="text-xs p-3 rounded-lg bg-rose-50 border border-rose-200 text-rose-800" data-testid="scan-error">
                <p className="font-semibold mb-1 flex items-center gap-1.5"><XCircle size={13} /> Scan failed</p>
                <p>{scanError}</p>
                <p className="mt-2 text-rose-700">Make sure the <Link to="/printer-setup" className="underline">Print Bridge</Link> is running on this device (it listens on <code>http://localhost:9101</code>).</p>
              </div>
            )}
            {scanResults && (
              <div className="space-y-2">
                <p className="text-xs font-medium" data-testid="scan-results-summary">
                  {(scanResults.printers || []).length} printer(s) discovered
                </p>
                {(scanResults.printers || []).length === 0 ? (
                  <p className="text-xs text-muted-foreground py-4 text-center">No printers found on this network. Make sure they&apos;re powered on and on the same Wi-Fi/LAN.</p>
                ) : (
                  <div className="divide-y border rounded-lg overflow-hidden" data-testid="scan-results-list">
                    {(scanResults.printers || []).map((p, i) => {
                      const ip = p.ip || p.address || p.host;
                      const port = p.port || 9100;
                      const name = p.name || p.model || p.service || `Printer @ ${ip}`;
                      const source = p.source || p.discovery || (p.via === 'mdns' ? 'mDNS' : 'TCP');
                      return (
                        <div key={`${ip}-${port}-${i}`} className="flex items-center justify-between gap-2 px-3 py-2 hover:bg-muted/30" data-testid={`scan-result-${i}`}>
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{name}</p>
                            <p className="text-[10px] text-muted-foreground font-mono">{ip}:{port} · via {source}</p>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs rounded-lg shrink-0"
                            onClick={() => {
                              // Hand control back to the parent: it owns
                              // the form / editing / sheetOpen state.
                              onPrefillFromScan(ip, port, name.length > 40 ? name.slice(0, 40) : name);
                            }}
                            data-testid={`scan-use-${i}`}
                          >
                            <Plus size={12} className="mr-1" />Use
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} data-testid="scan-close-btn">Close</Button>
          {iOSMode ? (
            <Button
              size="sm"
              disabled={ipadQuickSaving || !ipadQuickForm.ip_address.trim() || !ipadQuickForm.name.trim()}
              className="bg-orange-500 hover:bg-orange-600 text-white"
              onClick={async () => {
                setIpadQuickSaving(true);
                try {
                  const m = findModelById(ipadQuickForm.model);
                  const supportsAirPrint = m?.airprint !== false; // true / null both pass
                  await api.post('/cashier/printers', {
                    name: ipadQuickForm.name.trim(),
                    type: 'receipt',
                    ip_address: ipadQuickForm.ip_address.trim(),
                    port: 9100,
                    paper_width: ipadQuickForm.paper_width,
                    is_default: true,
                    auto_print: true,
                    copies: 1,
                    // BT-only models can't AirPrint → fall back to dialog
                    // with airprint disabled so the orchestrator doesn't
                    // route to the wrong path.
                    print_mode: 'dialog',
                    airprint_enabled: supportsAirPrint,
                    model: ipadQuickForm.model,
                  });
                  toast.success(
                    supportsAirPrint
                      ? `${ipadQuickForm.name} added · AirPrint enabled`
                      : `${ipadQuickForm.name} added · Bluetooth/USB only (no AirPrint)`
                  );
                  setIpadQuickForm(INITIAL_IPAD_QUICK_FORM);
                  onOpenChange(false);
                  if (onPrinterAdded) await onPrinterAdded();
                } catch (e) {
                  toast.error(e.response?.data?.detail || 'Failed to add printer');
                } finally {
                  setIpadQuickSaving(false);
                }
              }}
              data-testid="ipad-quick-save-btn"
            >
              {ipadQuickSaving ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Plus size={14} className="mr-1.5" />}
              Add printer
            </Button>
          ) : (
            <Button
              size="sm"
              disabled={scanRunning}
              className="bg-orange-500 hover:bg-orange-600 text-white"
              onClick={async () => {
                setScanRunning(true);
                setScanResults(null);
                setScanError(null);
                const r = await discoverPrinters();
                setScanRunning(false);
                if (r.ok) {
                  setScanResults(r.data || { printers: [] });
                } else {
                  setScanError(r.data?.error || `Bridge returned status ${r.status || 'no response'}`);
                }
              }}
              data-testid="scan-start-btn"
            >
              {scanRunning ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Radar size={14} className="mr-1.5" />}
              {scanResults ? 'Scan again' : 'Start scan'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
